/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useSelector } from 'react-redux';
import { encodeValue } from '../../utils/helper';

const VerifyPin = ({ onClose = () => {}, onSubmit = (pin = '', onValidate = false) => {}, isInvalid = false, onClear = () => {}, isLoading = false }) => {
	const user = useSelector((store) => store?.user);
	const [pin, setPin] = useState(new Array(6).fill(''));
	const inputRefs = useRef([]);
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const initialViewportHeight = useRef(window.visualViewport?.height || window.innerHeight);

	const handlePinChange = (value, index) => {
		if (/^[0-9]$/.test(value)) {
			const newPin = [...pin];
			newPin[index] = value;
			setPin(newPin);

      if (index < 5 && value) {
        inputRefs.current[index + 1]?.focus({ preventScroll: true });
      
        if (isKeyboardOpen) {
          inputRefs.current[index + 1]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } else {
          setTimeout(() => {
            inputRefs.current[index + 1]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 300); // beri waktu keyboard terbuka
        }
      }
		} else if (value === '') {
			const newPin = [...pin];
			newPin[index] = value;
			setPin(newPin);
		}
	};

  useEffect(() => {
    if (isInvalid) {
      setPin(new Array(6).fill(''));
      inputRefs.current[0]?.focus({ preventScroll: true });
    }
  }, [isInvalid]);

	const handleBackspace = (e, index) => {
		onClear();
		if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus({ preventScroll: true });
      inputRefs.current[index - 1]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
		}
	};

	useEffect(() => {
		if (pin.every((digit) => digit !== '') && user?.token) {
			setTimeout(() => {
				onSubmit(encodeValue(pin.join('')), true);
			}, 1000);
		}
	}, [pin, user?.token]);

	useEffect(() => {
		const handleFocus = () => setIsKeyboardOpen(true);
		const handleBlur = () => setIsKeyboardOpen(false);

		inputRefs.current.forEach((ref) => {
			ref?.addEventListener('focus', handleFocus);
			ref?.addEventListener('blur', handleBlur);
		});

		return () => {
			inputRefs.current.forEach((ref) => {
				ref?.removeEventListener('focus', handleFocus);
				ref?.removeEventListener('blur', handleBlur);
			});
		};
	}, []);

	useEffect(() => {
		if (!window.visualViewport) return;

		const handleResize = () => {
			const isKeyboardNowOpen = window.visualViewport.height < window.innerHeight;
			setIsKeyboardOpen(isKeyboardNowOpen);

			if (isKeyboardNowOpen) {
				document.body.style.overflow = 'hidden';
				document.body.style.position = 'fixed';
				document.body.style.width = '100%';
				document.documentElement.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = '';
				document.body.style.position = '';
				document.body.style.width = '';
				document.documentElement.style.overflow = '';
			}
		};

		window.visualViewport.addEventListener('resize', handleResize);
		return () => {
			window.visualViewport.removeEventListener('resize', handleResize);
		};
	}, []);

  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const keyboardNowOpen = currentHeight < initialViewportHeight.current - 100;

        setIsKeyboardOpen(keyboardNowOpen);
      }, 100);
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimeout);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

	const handleFocusLog = (index, e) => {
		const target = e?.target;
		if (!target || typeof target.getBoundingClientRect !== 'function') return;

		const rect = target.getBoundingClientRect();
		const time = new Date().toLocaleTimeString();
		const info = `
    Input ${index + 1} focused:
    - Height: ${Math.round(rect.height)}px
    - Width: ${Math.round(rect.width)}px
    - Top: ${Math.round(rect.top)}px
    - Left: ${Math.round(rect.left)}px
    - Current Value: ${pin[index] || '(empty)'}
    - Time: ${time}
    `.trim();
    console.log('info: ', info);
	};

	return (
		<div className={`w-full py-4 fixed top-0 left-0 right-0 z-40 ${isKeyboardOpen ? 'keyboard-open h-fit' : 'h-screen overflow-hidden'}`}>
			<div className='flex flex-row justify-between mx-4'>
				<div className='flex justify-center items-center cursor-pointer' onClick={() => {
          setPin(null)
          onClose()
        }}>
					<ArrowLeftIcon className={`${isLoading ? '!bg-transparent' : '!bg-white'} h-8 w-8 text-gray-900 rounded-full p-1.5`} />
				</div>
			</div>
			<div className={`${isLoading ? '!bg-transparent' : '!bg-white'} mt-4 px-5 flex flex-col`}>
				<div className=''>
					<div className='font-bold text-gray-800 text-2xl'>PIN</div>
					<div className='pt-2 text-gray-500 font-normal'>PIN akan digunakan untuk transaksi menggunakan Ultra Value dan Points.</div>
				</div>
				<div className='mt-8 flex justify-around md:justify-center gap-1 xxs:gap-2 h-16'>
					{pin.map((digit, index) => (
						<input
							key={index}
							ref={(el) => (inputRefs.current[index] = el)}
							id={`pin-${index}`}
							autoComplete='off'
							inputMode='numeric'
							type='password'
							maxLength='1'
							value={digit ? '*' : digit}
							onChange={(e) => handlePinChange(e.target.value, index)}
							onKeyDown={(e) => handleBackspace(e, index)}
							onFocus={(e) => handleFocusLog(index, e)}
							style={{
								width: `calc(100% / 6 - 4px)`,
							}}
							className={`!h-full text-center !border-1 !border-solid !border-gray-400 ${isLoading ? '!bg-transparent' : '!bg-white'} rounded-lg font-semibold text-gray-700 py-4 text-2xl`}
						/>
					))}
				</div>
				<div className={`${!isInvalid && 'hidden'} mt-5 text-red-500 text-base`}>Anda memasukkan PIN yang salah</div>
			</div>
		</div>
	);
};

export default VerifyPin;
